import { useEffect, useRef } from "react";
import {
    TerraDraw,
    TerraDrawPolygonMode,
    TerraDrawSelectMode
} from "terra-draw";
import { TerraDrawMapboxGLAdapter } from "terra-draw-mapbox-gl-adapter";
import type { AssignmentArea } from "../api/types";
import type { Map as MapboxMap } from "mapbox-gl";

interface PolygonDrawerProps {
    map: MapboxMap | null;
    initialArea?: AssignmentArea;
    onAreaChange?: (area: AssignmentArea | null) => void;
    enabled?: boolean;
}

// Store TerraDraw instance outside component to survive StrictMode remounts
let globalDrawInstance: TerraDraw | null = null;
let globalDrawMap: MapboxMap | null = null;

/**
 * Reads the polygon currently on the map straight from the live Terra Draw instance.
 * Callers that need the finished geometry (e.g. when the user stops drawing) should use
 * this instead of relying on the last "change" event having propagated through React state.
 */
export const getDrawnPolygonArea = (): AssignmentArea | null => {
    if (!globalDrawInstance) {
        return null;
    }

    try {
        const polygon = globalDrawInstance
            .getSnapshot()
            .find((feature) => feature.geometry.type === "Polygon");
        if (!polygon) {
            return null;
        }
        return { coordinates: polygon.geometry.coordinates as number[][][] };
    } catch (error) {
        console.warn("[PolygonDrawer] Could not read current polygon:", error);
        return null;
    }
};

/**
 * Component for drawing polygons on a Mapbox map
 * Uses Terra Draw for polygon drawing functionality (compatible with mapbox-gl v3)
 */
export const PolygonDrawer = ({
    map,
    initialArea,
    onAreaChange,
    enabled = true
}: PolygonDrawerProps): null => {
    const onAreaChangeRef = useRef(onAreaChange);
    const initialAreaRef = useRef(initialArea);

    // Keep refs up to date
    useEffect(() => {
        onAreaChangeRef.current = onAreaChange;
        initialAreaRef.current = initialArea;
    }, [onAreaChange, initialArea]);

    // Main initialization effect
    useEffect(() => {
        if (!map || !enabled) {
            return;
        }

        // If we have an existing instance for the same map, reuse it but reload state
        if (globalDrawInstance && globalDrawMap === map) {
            if (!globalDrawInstance.enabled) {
                globalDrawInstance.start();
            }
            // Clear leftover features from the previous session and reload initialArea
            try { globalDrawInstance.clear(); } catch (_) {}
            const reuseArea = initialAreaRef.current;
            if (reuseArea?.coordinates && reuseArea.coordinates.length > 0) {
                const results = globalDrawInstance.addFeatures([
                    {
                        type: "Feature",
                        geometry: { type: "Polygon", coordinates: reuseArea.coordinates },
                        properties: { mode: "polygon" }
                    }
                ]);
                globalDrawInstance.setMode("select");
                const featureId = results[0]?.id;
                if (featureId !== undefined) {
                    globalDrawInstance.selectFeature(featureId);
                }
            } else {
                globalDrawInstance.setMode("polygon");
            }
            return;
        }

        // If we have an instance for a different map, clean it up
        if (globalDrawInstance && globalDrawMap !== map) {
            globalDrawInstance.stop();
            globalDrawInstance = null;
            globalDrawMap = null;
        }

        const initializeDraw = () => {
            // Double-check we don't already have an instance
            if (globalDrawInstance && globalDrawMap === map) {
                return;
            }

            try {
                const draw = new TerraDraw({
                    adapter: new TerraDrawMapboxGLAdapter({ map }),
                    modes: [
                        new TerraDrawPolygonMode(),
                        new TerraDrawSelectMode({
                            flags: {
                                polygon: {
                                    feature: {
                                        draggable: false,
                                        coordinates: {
                                            midpoints: { draggable: true },
                                            draggable: true,
                                            deletable: true
                                        }
                                    }
                                }
                            }
                        })
                    ]
                });

                // Start Terra Draw
                draw.start();

                // Listen for changes
                draw.on("change", () => {
                    const snapshot = draw.getSnapshot();
                    const polygons = snapshot.filter(
                        (feature) => feature.geometry.type === "Polygon"
                    );

                    if (polygons.length > 0) {
                        const polygon = polygons[0];
                        const coords = polygon.geometry.coordinates as number[][][];
                        const area: AssignmentArea = { coordinates: coords };
                        onAreaChangeRef.current?.(area);
                    } else {
                        onAreaChangeRef.current?.(null);
                    }
                });

                globalDrawInstance = draw;
                globalDrawMap = map;

                // Load initial area if provided
                const initArea = initialAreaRef.current;
                if (initArea?.coordinates && initArea.coordinates.length > 0) {
                    const results = draw.addFeatures([
                        {
                            type: "Feature",
                            geometry: {
                                type: "Polygon",
                                coordinates: initArea.coordinates
                            },
                            properties: {
                                mode: "polygon"
                            }
                        }
                    ]);
                    draw.setMode("select");
                    // Auto-select the loaded polygon so vertex handles and midpoints
                    // are immediately visible — user can drag midpoints to add new vertices
                    const featureId = results[0]?.id;
                    if (featureId !== undefined) {
                        draw.selectFeature(featureId);
                    }
                } else {
                    draw.setMode("polygon");
                }
            } catch (error) {
                console.error("[PolygonDrawer] Error initializing Terra Draw:", error);
            }
        };

        // Check if style is loaded
        if (map.isStyleLoaded()) {
            initializeDraw();
        } else {
            const handleStyleLoad = () => {
                initializeDraw();
            };

            map.once("style.load", handleStyleLoad);

            // Fallback: check again after a delay (in case style.load already fired)
            const fallbackTimeout = setTimeout(() => {
                if (map.isStyleLoaded() && !globalDrawInstance) {
                    map.off("style.load", handleStyleLoad);
                    initializeDraw();
                }
            }, 200);

            return () => {
                map.off("style.load", handleStyleLoad);
                clearTimeout(fallbackTimeout);
            };
        }
    }, [map, enabled]);

    // Cleanup when disabled — clear features first so the polygon is removed from the map
    useEffect(() => {
        if (!enabled && globalDrawInstance) {
            try { globalDrawInstance.clear(); } catch (_) {}
            globalDrawInstance.stop();
            globalDrawInstance = null;
            globalDrawMap = null;
        }
    }, [enabled]);

    return null;
};
